import React, { useCallback, useRef, useState } from 'react';

interface Props {
  before: string;
  after: string;
  title: string;
  caption?: string;
  height?: number;
}

// Drag-to-reveal comparison. Pointer events cover mouse, touch and pen with one
// code path; the range input underneath keeps it operable by keyboard.
const BeforeAfterSlider: React.FC<Props> = ({ before, after, title, caption, height = 340 }) => {
  const [pos, setPos] = useState(50);
  const frameRef = useRef<HTMLDivElement>(null);
  const draggingRef = useRef(false);

  const setFromClientX = useCallback((clientX: number) => {
    const el = frameRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    if (rect.width === 0) return;
    const next = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.min(100, Math.max(0, next)));
  }, []);

  const onPointerDown = (e: React.PointerEvent) => {
    draggingRef.current = true;
    e.currentTarget.setPointerCapture(e.pointerId);
    setFromClientX(e.clientX);
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (!draggingRef.current) return;
    setFromClientX(e.clientX);
  };
  const endDrag = (e: React.PointerEvent) => {
    draggingRef.current = false;
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
  };

  return (
    <div>
      <div
        ref={frameRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        style={{
          position: 'relative', height: `${height}px`, borderRadius: '1rem',
          overflow: 'hidden', cursor: 'ew-resize', touchAction: 'none',
          boxShadow: '0 18px 44px rgba(5,8,15,0.24)', userSelect: 'none',
          background: '#050608',
        }}
      >
        <img
          src={after}
          alt={`${title} — after`}
          draggable={false}
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
        />
        <div style={{ position: 'absolute', inset: 0, width: `${pos}%`, overflow: 'hidden' }}>
          <img
            src={before}
            alt={`${title} — before`}
            draggable={false}
            style={{ position: 'absolute', inset: 0, width: `${100 / (pos / 100)}%`, height: '100%', objectFit: 'cover', maxWidth: 'none' }}
          />
        </div>

        {['before', 'after'].map(which => (
          <span
            key={which}
            style={{
              position: 'absolute', top: '0.75rem',
              left: which === 'before' ? '0.75rem' : 'auto',
              right: which === 'after' ? '0.75rem' : 'auto',
              background: 'rgba(5,6,8,0.72)', color: 'white',
              fontSize: '0.68rem', fontWeight: 800, letterSpacing: '0.08em',
              textTransform: 'uppercase', padding: '0.28rem 0.7rem', borderRadius: '9999px',
              pointerEvents: 'none',
            }}
          >{which}</span>
        ))}

        {/* Handle */}
        <div style={{ position: 'absolute', top: 0, bottom: 0, left: `${pos}%`, width: '3px', background: 'white', transform: 'translateX(-1.5px)', pointerEvents: 'none' }} />
        <div style={{
          position: 'absolute', top: '50%', left: `${pos}%`, transform: 'translate(-50%, -50%)',
          width: '2.6rem', height: '2.6rem', borderRadius: '50%', background: 'white',
          boxShadow: '0 6px 18px rgba(5,8,15,0.4)', display: 'flex', alignItems: 'center',
          justifyContent: 'center', gap: '3px', pointerEvents: 'none',
        }}>
          <span style={{ borderRight: '6px solid #0072c6', borderTop: '5px solid transparent', borderBottom: '5px solid transparent' }} />
          <span style={{ borderLeft: '6px solid #0072c6', borderTop: '5px solid transparent', borderBottom: '5px solid transparent' }} />
        </div>
      </div>

      <input
        type="range"
        min={0}
        max={100}
        value={pos}
        aria-label={`${title} — reveal before and after`}
        onChange={e => setPos(Number(e.target.value))}
        style={{ width: '100%', marginTop: '0.75rem', accentColor: '#0072c6' }}
      />
      <h3 style={{ fontWeight: 800, fontSize: '1rem', margin: '0.35rem 0 0.2rem', color: 'var(--text-primary)' }}>{title}</h3>
      {caption && <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', margin: 0, lineHeight: 1.6 }}>{caption}</p>}
    </div>
  );
};

export default BeforeAfterSlider;
